/**
 * Size Guide Page JavaScript
 * Handles the initialization and interaction for the size guide page
 * Extends the functionality of the size-chart.js modal for page display
 */

class SizeGuidePage {
  constructor() {
    this.sizeChart = null;
    this.initializationAttempts = 0;
    this.maxAttempts = 10;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () =>
        this.initializeSizeChart(),
      );
    } else {
      this.initializeSizeChart();
    }

    // Backup initialization on window load
    window.addEventListener("load", () => this.ensureInitialization());
  }

  initializeSizeChart() {
    this.initializationAttempts++;

    if (typeof SizeChart !== "undefined") {
      try {
        // Initialize the size chart
        this.sizeChart = new SizeChart();
        window.sizeChart = this.sizeChart; // Make it globally accessible

        // Set up page-specific behaviors
        this.setupPageInterface();
        this.setupEventListeners();
        this.forceVisibility();

        console.log("Size guide page initialized successfully");
      } catch (error) {
        console.error("Error initializing size chart:", error);
        this.retryInitialization();
      }
    } else {
      console.warn(
        `SizeChart class not found (attempt ${this.initializationAttempts})`,
      );
      this.retryInitialization();
    }
  }

  retryInitialization() {
    if (this.initializationAttempts < this.maxAttempts) {
      setTimeout(() => this.initializeSizeChart(), 300);
    } else {
      console.error("Failed to initialize SizeChart after maximum attempts");
      this.setupFallbackInterface();
    }
  }

  setupPageInterface() {
    console.log("Setting up page interface...");

    // Remove modal-specific styles and behaviors
    this.removeModalBehaviors();

    // Force show interface elements
    this.showInterfaceElements();

    // Initialize gender tabs with all available genders initially
    this.initializeInitialGenderTabs();

    // Set up product type selector to trigger gender tab updates
    const productSelector = document.getElementById(
      "size-guide-product-dropdown",
    );

    // Hardcode Oversized as default product
    if (productSelector) {
      productSelector.value = "oversized";
      this.updateGenderTabsForProduct("oversized");
    }

    console.log("Page interface setup complete");
  }

  initializeInitialGenderTabs() {
    // Create initial tabs with all possible genders
    const tabsContainer = document.getElementById("size-guide-gender-tabs");
    if (!tabsContainer) return;

    // Hide tabs initially since no product is selected yet
    tabsContainer.style.display = "none";

    // Only create initial tabs if container is empty
    if (!tabsContainer.innerHTML.trim()) {
      const allGenders = ["unisex", "male"];

      tabsContainer.innerHTML = "";
      allGenders.forEach((gender, index) => {
        const tab = document.createElement("button");
        tab.className = `size-guide-gender-tab${index === 0 ? " active" : ""}`;
        tab.setAttribute("role", "tab");
        tab.setAttribute("aria-selected", index === 0 ? "true" : "false");
        tab.setAttribute("data-gender", gender);

        const displayName = gender.charAt(0).toUpperCase() + gender.slice(1);
        tab.textContent = displayName;

        tab.addEventListener("click", (e) => {
          e.preventDefault();
          this.handleGenderTabClick(gender, tab);
        });

        tabsContainer.appendChild(tab);
      });
    }
  }

  removeModalBehaviors() {
    const modal = document.getElementById("SizeChartModal");
    if (modal) {
      modal.style.display = "none";
    }

    // Override any modal-specific event listeners
    document.removeEventListener("keydown", this.handleModalKeydown);
  }

  showInterfaceElements() {
    const elements = [
      { id: "size-guide-gender-tabs", display: "flex" },
      { id: "size-guide-product-selector", display: "block" },
      { id: "size-guide-table-container", display: "block" },
      { id: "size-guide-measurement-section", display: "block" },
    ];

    elements.forEach(({ id, display }) => {
      const element = document.getElementById(id);
      if (element) {
        element.style.display = display;
        element.style.visibility = "visible";
        element.style.opacity = "1";
        element.style.position = "static";
        element.style.transform = "none";
        console.log(`Showing element: ${id}`);
      } else {
        console.warn(`Element not found: ${id}`);
      }
    });

    // Also make sure the measurement section is visible
    this.initializeMeasurementSection();
  }

  initializeMeasurementSection() {
    const measurementSection = document.getElementById(
      "size-guide-measurement-section",
    );
    if (measurementSection) {
      measurementSection.style.display = "block";
      console.log("Measurement section found and displayed");

      // Check what measurement images are available
      const allImages = document.querySelectorAll(
        ".size-guide-measurement-image",
      );
      console.log("Found measurement images:", allImages.length);

      allImages.forEach((img, index) => {
        console.log(`Image ${index + 1}:`, img.id, img.style.display);
      });

      // Show the first available measurement image by default
      const tshirtImage = document.getElementById("tshirt-measurement");
      if (tshirtImage) {
        tshirtImage.style.display = "block";
        console.log("Initialized with t-shirt measurement image");

        // Check if the actual img element exists
        const imgElement = tshirtImage.querySelector("img");
        if (imgElement) {
          console.log("T-shirt img element found, src:", imgElement.src);

          // Add error handling for image loading
          imgElement.addEventListener("error", function () {
            console.error(
              "Failed to load t-shirt measurement image:",
              this.src,
            );
          });

          imgElement.addEventListener("load", function () {
            console.log("T-shirt measurement image loaded successfully");
          });
        } else {
          console.error("T-shirt img element not found");
        }
      } else {
        console.error("T-shirt measurement element not found");
      }
    } else {
      console.error("Measurement section not found");
    }
  }

  forceVisibility() {
    // Set up periodic checks for the first few seconds
    let checkCount = 0;
    const maxChecks = 8; // 4 seconds worth of checks

    const visibilityCheck = setInterval(() => {
      checkCount++;

      const tabs = document.getElementById("size-guide-gender-tabs");
      const productTypes = document.getElementById(
        "size-guide-product-selector",
      );

      // Check if tabs are empty or hidden
      if (
        tabs &&
        (!tabs.innerHTML.trim() ||
          tabs.style.display === "none" ||
          !tabs.offsetParent)
      ) {
        this.refreshGenderTabs();
        tabs.style.display = "flex";
        tabs.style.visibility = "visible";
      }

      // Ensure product types are visible
      if (
        productTypes &&
        (productTypes.style.display === "none" || !productTypes.offsetParent)
      ) {
        productTypes.style.display = "block";
        productTypes.style.visibility = "visible";
      }

      if (checkCount >= maxChecks) {
        clearInterval(visibilityCheck);
      }
    }, 500);
  }

  refreshGenderTabs() {
    if (
      this.sizeChart &&
      typeof this.sizeChart.createGenderTabs === "function"
    ) {
      try {
        this.sizeChart.createGenderTabs();
      } catch (error) {
        console.warn("Error refreshing gender tabs:", error);
      }
    }
  }

  setupEventListeners() {
    // Handle product type changes
    const productSelector = document.getElementById(
      "size-guide-product-dropdown",
    );
    if (productSelector) {
      productSelector.addEventListener("change", (e) => {
        this.handleProductTypeChange(e.target.value);
      });
    }

    // Handle window resize
    window.addEventListener(
      "resize",
      this.debounce(() => {
        this.handleResize();
      }, 250),
    );

    // Handle visibility change (tab switching)
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) {
        setTimeout(() => this.ensureInitialization(), 100);
      }
    });
  }

  handleProductTypeChange(productType) {
    console.log("Product type changed to:", productType);

    const tabsContainer = document.getElementById("size-guide-gender-tabs");

    // Hide tabs if no product is selected
    if (!productType) {
      if (tabsContainer) {
        tabsContainer.style.display = "none";
      }
      return;
    }

    // Show measurement section when a product is selected
    const measurementSection = document.getElementById(
      "size-guide-measurement-section",
    );
    if (measurementSection && productType) {
      measurementSection.style.display = "block";
      console.log("Measurement section shown");
    }

    // Show appropriate measurement images
    this.showMeasurementImages(productType);

    // Update gender tabs based on available data for this product type
    this.updateGenderTabsForProduct(productType);
  }

  showMeasurementImages(productType) {
    // Hide all measurement images first
    const allImages = document.querySelectorAll(
      ".size-guide-measurement-image",
    );
    allImages.forEach((img) => {
      img.style.display = "none";
    });

    if (!productType) return;

    // Show appropriate image based on product type
    let imageToShow = null;

    if (productType === "oversized") {
      imageToShow = document.getElementById("tshirt-measurement");
    } else if (productType === "polo") {
      imageToShow = document.getElementById("polo-measurement");
    } else if (productType.includes("shirt")) {
      imageToShow = document.getElementById("shirt-measurement");
    }

    if (imageToShow) {
      imageToShow.style.display = "block";
      console.log("Showing measurement image for:", productType);
    } else {
      // Fallback to t-shirt measurement if no specific image found
      const fallbackImage = document.getElementById("tshirt-measurement");
      if (fallbackImage) {
        fallbackImage.style.display = "block";
        console.log("Showing fallback t-shirt measurement image");
      }
    }
  }

  updateGenderTabsForProduct(productType) {
    if (!this.sizeChart || !this.sizeChart.sizeChartData || !productType) {
      return;
    }

    const productData = this.sizeChart.sizeChartData[productType];
    if (!productData) {
      return;
    }

    const tabsContainer = document.getElementById("size-guide-gender-tabs");
    if (!tabsContainer) {
      return;
    }

    // Get available genders for this product type
    const availableGenders = Object.keys(productData);

    // Clear existing tabs
    tabsContainer.innerHTML = "";

    // Create tabs only for available genders
    availableGenders.forEach((gender, index) => {
      const tab = document.createElement("button");
      tab.className = `size-guide-gender-tab${index === 0 ? " active" : ""}`;
      tab.setAttribute("role", "tab");
      tab.setAttribute("aria-selected", index === 0 ? "true" : "false");
      tab.setAttribute("data-gender", gender);

      // Capitalize gender name for display
      const displayName = gender.charAt(0).toUpperCase() + gender.slice(1);
      tab.textContent = displayName;

      // Add click event listener
      tab.addEventListener("click", (e) => {
        e.preventDefault();
        this.handleGenderTabClick(gender, tab);
      });

      tabsContainer.appendChild(tab);
    });

    // Always show the tabs container
    tabsContainer.style.display = "flex";

    // Update the size chart table with the first available gender
    this.updateSizeChartTable(productType, availableGenders[0]);
  }

  updateSizeChartTable(productType, gender) {
    if (!this.sizeChart || !this.sizeChart.sizeChartData) return;

    const productData = this.sizeChart.sizeChartData[productType];
    if (!productData || !productData[gender]) return;

    const data = productData[gender];
    const tableContainer = document.getElementById(
      "size-guide-table-container",
    );
    const loadingDiv = document.getElementById("size-guide-loading");

    if (!tableContainer) return;

    // Hide loading, show table
    if (loadingDiv) loadingDiv.style.display = "none";

    // Create or update table
    let table = document.getElementById("size-guide-table");
    if (!table) {
      table = document.createElement("table");
      table.id = "size-guide-table";
      table.className = "size-guide-table";
      tableContainer.appendChild(table);
    }

    // Clear and rebuild table
    table.innerHTML = "";

    // Create header
    const thead = document.createElement("thead");
    const headerRow = document.createElement("tr");
    data.headers.forEach((header) => {
      const th = document.createElement("th");
      th.textContent = header;
      headerRow.appendChild(th);
    });
    thead.appendChild(headerRow);
    table.appendChild(thead);

    // Create body
    const tbody = document.createElement("tbody");
    data.sizes.forEach((sizeRow) => {
      const tr = document.createElement("tr");
      sizeRow.forEach((cellData) => {
        const td = document.createElement("td");
        td.textContent = cellData;
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);

    table.style.display = "table";
  }

  handleGenderTabClick(gender, clickedTab) {
    // Remove active class from all tabs
    const allTabs = document.querySelectorAll(".size-guide-gender-tab");
    allTabs.forEach((tab) => {
      tab.classList.remove("active");
      tab.setAttribute("aria-selected", "false");
    });

    // Add active class to clicked tab
    clickedTab.classList.add("active");
    clickedTab.setAttribute("aria-selected", "true");

    // Get current product type
    const productSelector = document.getElementById(
      "size-guide-product-dropdown",
    );
    const currentProduct = productSelector ? productSelector.value : "";

    // Update size chart table
    if (currentProduct) {
      this.updateSizeChartTable(currentProduct, gender);
    }
  }

  handleResize() {
    // Ensure elements remain visible after resize
    this.showInterfaceElements();
  }

  ensureInitialization() {
    const tabs = document.getElementById("size-guide-gender-tabs");
    const productTypes = document.getElementById("size-guide-product-selector");

    // If elements are not visible or empty, try to reinitialize
    if (
      !this.sizeChart ||
      (tabs && (!tabs.innerHTML.trim() || !tabs.offsetParent)) ||
      (productTypes && !productTypes.offsetParent)
    ) {
      if (this.initializationAttempts < this.maxAttempts) {
        console.log("Re-initializing size guide due to missing elements");
        this.initializeSizeChart();
      } else {
        this.setupFallbackInterface();
      }
    }
  }

  setupFallbackInterface() {
    console.log("Setting up fallback interface");

    const tabs = document.getElementById("size-guide-gender-tabs");
    if (tabs && !tabs.innerHTML.trim()) {
      const allGenders = ["unisex", "male"];

      tabs.innerHTML = "";
      allGenders.forEach((gender, index) => {
        const tab = document.createElement("button");
        tab.className = `size-guide-gender-tab${index === 0 ? " active" : ""}`;
        tab.setAttribute("role", "tab");
        tab.setAttribute("aria-selected", index === 0 ? "true" : "false");
        tab.setAttribute("data-gender", gender);

        const displayName = gender.charAt(0).toUpperCase() + gender.slice(1);
        tab.textContent = displayName;

        tab.addEventListener("click", (e) => {
          e.preventDefault();
          this.handleGenderTabClick(gender, tab);
        });

        tabs.appendChild(tab);
      });
    }

    // Ensure all elements are visible
    this.showInterfaceElements();
  }

  // Utility function to debounce events
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Initialize the size guide page when the script loads
if (typeof window !== "undefined") {
  window.sizeGuidePage = new SizeGuidePage();
}
